import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  BedDouble,
  Building2,
  CalendarDays,
  Car,
  ClipboardList,
  Clock3,
  Home,
  MapPinned,
  Stethoscope,
  UserRound,
  Users,
  UtensilsCrossed,
} from 'lucide-react';
import { getHospitalReportById } from '../services/api';
import '../styles/HospitalReportDetail.css';

const BUSYNESS_LABELS = {
  1: 'かなり余裕あり',
  2: 'やや余裕あり',
  3: '標準',
  4: '忙しい',
  5: 'かなり忙しい',
};

const ASSIGNMENT_BURDEN_LABELS = {
  1: 'かなり重い',
  2: 'やや重い',
  3: '標準',
  4: 'やや軽い',
  5: 'ほぼ負担なし',
};

const SELF_STUDY_LABELS = {
  1: 'ほぼ確保できない',
  2: 'あまり確保できない',
  3: '標準',
  4: '確保しやすい',
  5: 'かなり確保しやすい',
};

const HANDS_ON_LABELS = {
  1: '見学中心',
  2: 'やや少ない',
  3: '標準',
  4: '多い',
  5: 'かなり多い',
};

const TEACHING_LABELS = {
  1: 'ほぼ教えてもらえない',
  2: '最低限',
  3: '標準',
  4: '丁寧',
  5: '非常に教育的',
};

const QUESTION_LABELS = {
  1: 'かなり聞きにくい',
  2: 'やや聞きにくい',
  3: '標準',
  4: '聞きやすい',
  5: 'とても聞きやすい',
};

const ATMOSPHERE_LABELS = {
  1: 'かなり悪い',
  2: 'やや悪い',
  3: '標準',
  4: '良い',
  5: '非常に良い',
};

const PERSONALITY_LABELS = {
  1: 'かなり厳しい',
  2: 'やや厳しい',
  3: '標準',
  4: '良い',
  5: '非常に良い',
};

const LODGING_LABELS = {
  1: 'かなり不便',
  2: 'やや不便',
  3: '標準',
  4: '便利',
  5: 'かなり快適',
};

const CAR_LABELS = {
  1: '不要',
  2: 'あまり不要',
  3: 'あると安心',
  4: 'かなり必要',
  5: '必須',
};

const CLEANLINESS_LABELS = {
  1: 'かなり気になる',
  2: 'やや気になる',
  3: '標準',
  4: 'きれい',
  5: 'かなりきれい',
};

const FREE_STUDY_TIME_LABELS = {
  over_three_hours: '3時間以上',
  one_to_three_hours: '1〜3時間',
  thirty_to_sixty_minutes: '30〜60分',
  almost_none: 'ほぼなし',
  none: 'なし',
};

const TOUR_OPTION_LABELS = {
  possible: '見学可',
  impossible: '見学不可',
  unknown: '見学不明',
  negotiable: '要相談',
};

const MEAL_LABELS = {
  none: 'なし',
  free: '無料',
  paid: '有料',
};

function firstDefined(...values) {
  return values.find((value) => value !== null && value !== undefined && value !== '');
}

function hasMeaningfulContent(value) {
  if (!value || typeof value !== 'string') return false;
  const plain = value.replace(/<[^>]*>/g, ' ').replace(/&nbsp;/g, ' ').trim();
  return plain.length > 0;
}

function formatDateTime(value) {
  if (!value) return '未設定';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '未設定';
  return date.toLocaleString('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getMealText(value) {
  return MEAL_LABELS[value] || '未設定';
}

function getTourText(value) {
  return TOUR_OPTION_LABELS[value] || '未設定';
}

function getBooleanText(value) {
  if (value === true) return 'あり';
  if (value === false) return 'なし';
  return '未設定';
}

function RatingDots({ value }) {
  const safeValue = Number(value);
  if (!safeValue) {
    return <span className="detail-rating-empty">未設定</span>;
  }

  return (
    <span className="detail-rating-dots" aria-hidden="true">
      {Array.from({ length: 5 }, (_, index) => (
        <span key={index} className={`detail-rating-dot ${index < safeValue ? 'is-active' : ''}`} />
      ))}
    </span>
  );
}

function DetailHtmlBlock({ html, emptyText = '記載なし' }) {
  if (!hasMeaningfulContent(html)) {
    return <div className="detail-empty-box">{emptyText}</div>;
  }

  return <div className="read-only-content" dangerouslySetInnerHTML={{ __html: html }} />;
}

function HospitalReportDetail() {
  const { report_id } = useParams();
  const [report, setReport] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setIsLoading(true);
        setError('');
        const data = await getHospitalReportById(report_id);
        setReport(data);
      } catch (fetchError) {
        console.error('レポート詳細の取得に失敗しました:', fetchError);
        setError('レポート詳細の取得に失敗しました。');
      } finally {
        setIsLoading(false);
      }
    };

    fetchReport();
  }, [report_id]);

  const headerCards = report
    ? [
        { key: 'hospital', icon: Building2, label: '病院', value: report.hospital_name || '未設定' },
        { key: 'department', icon: Stethoscope, label: '科', value: report.department_name || '未設定' },
        { key: 'term', icon: CalendarDays, label: 'ターム', value: report.term ? `ターム ${report.term}` : '未設定' },
        { key: 'cohort', icon: Users, label: 'コホート', value: report.cohort_name || '未設定' },
      ]
    : [];

  const evaluationItems = report
    ? [
        { key: 'busyness', label: '忙しさ', value: report.busyness, text: BUSYNESS_LABELS[report.busyness] },
        { key: 'assignment_burden', label: '課題・発表の負担度', value: report.assignment_burden, text: ASSIGNMENT_BURDEN_LABELS[report.assignment_burden] },
        { key: 'self_study_availability', label: '自習時間の確保しやすさ', value: report.self_study_availability, text: SELF_STUDY_LABELS[report.self_study_availability] },
        { key: 'hands_on_opportunity', label: '手技・実践機会', value: report.hands_on_opportunity, text: HANDS_ON_LABELS[report.hands_on_opportunity] },
        { key: 'teaching_quality', label: '教育の質', value: firstDefined(report.teaching_quality, report.doctor_eval), text: TEACHING_LABELS[firstDefined(report.teaching_quality, report.doctor_eval)] },
        { key: 'question_approachability', label: '質問のしやすさ', value: firstDefined(report.question_approachability, report.department_eval), text: QUESTION_LABELS[firstDefined(report.question_approachability, report.department_eval)] },
        { key: 'department_atmosphere', label: '医局の雰囲気', value: firstDefined(report.department_atmosphere, report.department_eval), text: ATMOSPHERE_LABELS[firstDefined(report.department_atmosphere, report.department_eval)] },
        { key: 'doctor_personality', label: '医師の人柄', value: firstDefined(report.doctor_personality, report.doctor_eval), text: PERSONALITY_LABELS[firstDefined(report.doctor_personality, report.doctor_eval)] },
        { key: 'lodging_convenience', label: '宿舎の利便性・快適さ', value: firstDefined(report.lodging_convenience, report.lodging_cleanliness), text: LODGING_LABELS[firstDefined(report.lodging_convenience, report.lodging_cleanliness)] },
        { key: 'car_requirement', label: '車の必要性', value: report.car_requirement, text: CAR_LABELS[report.car_requirement] },
      ].filter((item) => item.value !== null && item.value !== undefined && item.value !== '')
    : [];

  const legacyItems = report
    ? [
        { key: 'doctor_eval', label: '指導医の総合評価', value: report.doctor_eval, text: TEACHING_LABELS[report.doctor_eval] || ATMOSPHERE_LABELS[report.doctor_eval] },
        { key: 'department_eval', label: '医局・グループの総合評価', value: report.department_eval, text: ATMOSPHERE_LABELS[report.department_eval] },
        { key: 'hospital_cleanliness', label: '病院の清潔さ', value: report.hospital_cleanliness, text: CLEANLINESS_LABELS[report.hospital_cleanliness] },
        { key: 'lodging_cleanliness', label: '宿舎の清潔さ', value: report.lodging_cleanliness, text: CLEANLINESS_LABELS[report.lodging_cleanliness] },
      ].filter((item) => item.value !== null && item.value !== undefined && item.value !== '')
    : [];

  const busynessFacts = report
    ? [
        report.typical_start_time ? `始業 ${report.typical_start_time}` : null,
        report.typical_end_time ? `終業 ${report.typical_end_time}` : null,
        report.late_days_per_week !== null && report.late_days_per_week !== undefined ? `17時以降 ${report.late_days_per_week}日/週` : null,
        report.free_study_time ? `自由時間 ${FREE_STUDY_TIME_LABELS[report.free_study_time] || report.free_study_time}` : null,
        report.has_night_duty !== null && report.has_night_duty !== undefined ? `当直・夜遅い日 ${report.has_night_duty ? 'あり' : 'なし'}` : null,
      ].filter(Boolean)
    : [];

  const doctors = report?.doctors_json?.length ? report.doctors_json : report?.doctors?.length ? report.doctors : [];

  if (isLoading) {
    return <div className="hospital-report-detail-container detail-loading">読み込み中...</div>;
  }

  if (error) {
    return <div className="hospital-report-detail-container detail-error">{error}</div>;
  }

  if (!report) {
    return null;
  }

  return (
    <div className="hospital-report-detail-container">
      <div className="detail-topbar">
        <Link to="/hospital-report-list" className="detail-back-link">
          <ArrowLeft size={18} />
          一覧に戻る
        </Link>
        <div className="detail-updated-at">更新日時: {formatDateTime(report.updated_at)}</div>
      </div>

      <header className="detail-hero">
        <div>
          <h1 className="detail-title">申し送り詳細</h1>
          <br />
        </div>
        <div className="detail-header-cards">
          {headerCards.map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.key} className="detail-header-card">
                <div className="detail-header-card-label">
                  <Icon size={16} />
                  {item.label}
                </div>
                <div className="detail-header-card-value">{item.value}</div>
              </div>
            );
          })}
        </div>
      </header>

      <section className="detail-section">
        <h2 className="detail-section-title">全体評価</h2>
        <div className="detail-rating-grid">
          {evaluationItems.length > 0 ? (
            evaluationItems.map((item) => (
              <div key={item.key} className="detail-rating-card">
                <div className="detail-rating-label">{item.label}</div>
                <div className="detail-rating-text">{item.text || '未設定'}</div>
                <RatingDots value={item.value} />
              </div>
            ))
          ) : (
            <div className="detail-empty-box">評価情報はまだ登録されていません。</div>
          )}
        </div>

        <div className="detail-chip-grid">
          <div className="detail-chip-card">
            <Clock3 size={16} />
            <span className="detail-chip-label">病院見学</span>
            <strong>{getTourText(report.hospital_tour_option)}</strong>
          </div>
          <div className="detail-chip-card">
            <UtensilsCrossed size={16} />
            <span className="detail-chip-label">食事提供</span>
            <strong>{`朝 ${getMealText(report.breakfast)} / 昼 ${getMealText(report.lunch)} / 夕 ${getMealText(report.dinner)}`}</strong>
          </div>
          <div className="detail-chip-card">
            <Car size={16} />
            <span className="detail-chip-label">交通費支給</span>
            <strong>{getBooleanText(report.transport)}</strong>
          </div>
        </div>

        {busynessFacts.length > 0 && (
          <div className="detail-fact-box">
            <div className="detail-fact-title">忙しさの参考情報</div>
            <div className="detail-fact-list">
              {busynessFacts.map((fact) => (
                <span key={fact} className="detail-fact-chip">
                  {fact}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      {legacyItems.length > 0 && (
        <section className="detail-section">
          <h2 className="detail-section-title">旧フォームの補足評価</h2>
          <div className="detail-rating-grid legacy-grid">
            {legacyItems.map((item) => (
              <div key={item.key} className="detail-rating-card">
                <div className="detail-rating-label">{item.label}</div>
                <div className="detail-rating-text">{item.text || '未設定'}</div>
                <RatingDots value={item.value} />
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="detail-section">
        <h2 className="detail-section-title">実習内容</h2>
        <div className="detail-content-grid">
          <div className="detail-panel">
            <div className="detail-panel-title"><CalendarDays size={16} /> スケジュール</div>
            <DetailHtmlBlock html={report.schedule} />
          </div>
          <div className="detail-panel">
            <div className="detail-panel-title"><ClipboardList size={16} /> 実習内容</div>
            <DetailHtmlBlock html={report.training} />
          </div>
          <div className="detail-panel">
            <div className="detail-panel-title"><ClipboardList size={16} /> 課題・発表</div>
            {report.has_assignment ? (
              <DetailHtmlBlock html={report.assignment} emptyText="課題ありの設定ですが内容は未記入です。" />
            ) : (
              <div className="detail-empty-box">課題・発表なし</div>
            )}
          </div>
          <div className="detail-panel">
            <div className="detail-panel-title"><MapPinned size={16} /> 病院見学メモ</div>
            <DetailHtmlBlock html={report.hospital_tour_text} />
          </div>
        </div>
      </section>

      <section className="detail-section">
        <h2 className="detail-section-title">指導医・医局</h2>
        {doctors.length > 0 ? (
          <div className="detail-doctor-grid">
            {doctors.map((doctor, index) => (
              <div key={`${doctor.doctor_name || 'doctor'}-${index}`} className="detail-doctor-card">
                <div className="detail-doctor-name">
                  <UserRound size={16} />
                  {doctor.doctor_name || `医師 ${index + 1}`}
                </div>
                <DetailHtmlBlock html={doctor.doctor_note} emptyText="メモなし" />
              </div>
            ))}
          </div>
        ) : (
          <div className="detail-empty-box">医師情報の記載はありません。</div>
        )}
      </section>

      <section className="detail-section">
        <h2 className="detail-section-title">設備・生活・補足</h2>
        <div className="detail-content-grid">
          <div className="detail-panel">
            <div className="detail-panel-title"><Home size={16} /> 設備・宿舎</div>
            <DetailHtmlBlock html={report.facility} />
          </div>
          <div className="detail-panel">
            <div className="detail-panel-title"><BedDouble size={16} /> その他メモ</div>
            <DetailHtmlBlock html={report.other} />
          </div>
        </div>
      </section>
    </div>
  );
}

export default HospitalReportDetail;
